import React, { useMemo, useState } from "react";
import {
  View,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import FastImage from "react-native-fast-image";
import Text from "@components/CustomText";
import ChevronRight from "@assets/svg/ChevronRight";
import { tokens, sharedStyles } from "../styles";

// Inventory section — surfaces TOP_10_SKUS items the executive's customers
// have on this route, grouped per-customer.
//
// The endpoint returns sku-shaped rows, each with a `per_cx` array
// breaking the sku's order_items down by customer. We pivot to customer-
// first here so a DB who's about to deliver to customer A can mark and
// pack only A's items without touching B/C/D. Each customer card expands
// to the list of skus they own; "Pack Visible Items" inside the dropdown
// packs only that customer's unpacked order_item_ids; "Mark Issue" on a
// sku row opens the marking sheet scoped to that customer (the sheet's
// stepper caps to the per-customer unpacked count and submits with the
// `order_item_ids` array so the backend intersects).
//
// Hidden when zero customers come back — keeps the screen clean for the
// 6-7 spokes that actually run the top-sku stash flow.

const sumBy = (arr, key) =>
  (arr || []).reduce((acc, x) => acc + (Number(x?.[key]) || 0), 0);

// Pivot sku-grouped rows into per-customer rows:
//   [{cx_id, cx_name, total_qty, packed_qty, unpacked_qty,
//     unpacked_order_item_ids, skus: [{sku_id, sku_name, ...}]}]
const pivotByCustomer = (skus) => {
  const byCx = new Map();
  (skus || []).forEach((sku) => {
    const perCx = Array.isArray(sku.per_cx) ? sku.per_cx : [];
    perCx.forEach((cx) => {
      const key = String(cx.cx_id);
      if (!byCx.has(key)) {
        byCx.set(key, {
          cx_id: cx.cx_id,
          cx_name: cx.cx_name || cx.cx_id || "",
          skus: [],
        });
      }
      byCx.get(key).skus.push({
        sku_id: sku.sku_id,
        sku_name: sku.sku_name,
        product_image: sku.product_image || null,
        cx_id: cx.cx_id,
        cx_name: cx.cx_name,
        total_qty: Number(cx.total_qty || 0),
        packed_qty: Number(cx.packed_qty || 0),
        unpacked_qty: Number(cx.unpacked_qty || 0),
        order_item_ids: Array.isArray(cx.order_item_ids)
          ? cx.order_item_ids
          : [],
        unpacked_order_item_ids: Array.isArray(cx.unpacked_order_item_ids)
          ? cx.unpacked_order_item_ids
          : [],
      });
    });
  });
  return Array.from(byCx.values()).map((c) => {
    const total = c.skus.reduce((s, x) => s + x.total_qty, 0);
    const packed = c.skus.reduce((s, x) => s + x.packed_qty, 0);
    const unpacked = c.skus.reduce((s, x) => s + x.unpacked_qty, 0);
    const unpackedIds = c.skus.flatMap((x) => x.unpacked_order_item_ids);
    return {
      ...c,
      total_qty: total,
      packed_qty: packed,
      unpacked_qty: unpacked,
      unpacked_order_item_ids: unpackedIds,
    };
  });
};

const CustomerCard = ({ customer, submitting, onPackCustomer, onMarkSku }) => {
  const [open, setOpen] = useState(false);
  const unpackedIds = customer.unpacked_order_item_ids || [];
  const packDisabled = submitting || unpackedIds.length === 0;
  return (
    <View style={[sharedStyles.card, styles.cxCard]}>
      <TouchableOpacity
        activeOpacity={0.85}
        onPress={() => setOpen((v) => !v)}
        style={styles.cxHeader}
      >
        <View style={styles.cxHeaderLeft}>
          <Text variant="title_16" style={styles.cxName} numberOfLines={1}>
            {customer.cx_name || customer.cx_id || "—"}
          </Text>
          <Text variant="caption_2" style={styles.cxMeta}>
            {customer.skus.length} SKU
            {customer.skus.length === 1 ? "" : "s"} · {customer.total_qty} unit
            {customer.total_qty === 1 ? "" : "s"}
          </Text>
          <Text variant="caption_2" style={styles.cxStats}>
            ✓ {customer.packed_qty} packed · ◯ {customer.unpacked_qty} unpacked
          </Text>
        </View>
        <View style={[styles.chev, open ? styles.chevUp : styles.chevDown]}>
          <ChevronRight
            width={12}
            height={12}
            pathFill={tokens.textSecondary}
          />
        </View>
      </TouchableOpacity>
      {open ? (
        <View style={styles.cxBody}>
          {customer.skus.map((s) => (
            <View key={`${customer.cx_id}-${s.sku_id}`} style={styles.skuRow}>
              {s.product_image ? (
                <FastImage
                  source={{ uri: s.product_image }}
                  style={styles.skuThumb}
                  resizeMode={FastImage.resizeMode.cover}
                />
              ) : (
                <View style={[styles.skuThumb, styles.skuThumbPlaceholder]} />
              )}
              <View style={styles.skuRowLeft}>
                <Text
                  variant="caption_2"
                  style={styles.skuRowTitle}
                  numberOfLines={2}
                >
                  {s.sku_name || s.sku_id}
                </Text>
                <Text variant="caption_2" style={styles.skuRowMeta}>
                  {s.total_qty} unit{s.total_qty === 1 ? "" : "s"} · ✓{" "}
                  {s.packed_qty} · ◯ {s.unpacked_qty}
                </Text>
              </View>
              {s.unpacked_qty > 0 && onMarkSku ? (
                <TouchableOpacity
                  activeOpacity={0.85}
                  onPress={() => onMarkSku(s)}
                  style={styles.markBtn}
                >
                  <Text variant="caption_2" style={styles.markBtnText}>
                    Mark Issue
                  </Text>
                </TouchableOpacity>
              ) : null}
            </View>
          ))}
          <TouchableOpacity
            activeOpacity={0.85}
            onPress={() => onPackCustomer(customer)}
            disabled={packDisabled}
            style={[styles.packBtn, packDisabled && styles.packBtnIdle]}
          >
            {submitting ? (
              <ActivityIndicator color={tokens.primary.fg} />
            ) : (
              <Text variant="caption_2" style={styles.packBtnText}>
                Pack Visible Items ({unpackedIds.length})
              </Text>
            )}
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  );
};

const InventorySection = ({
  skus,
  loading,
  submitting,
  onPackAll,
  onMarkSku,
}) => {
  const [open, setOpen] = useState(true);
  const totalUnits = useMemo(() => sumBy(skus, "line_count"), [skus]);
  const totalPacked = useMemo(() => sumBy(skus, "packed_qty"), [skus]);
  const customers = useMemo(() => pivotByCustomer(skus), [skus]);

  if (!loading && (!Array.isArray(skus) || skus.length === 0)) return null;

  const handlePackCustomer = (customer) => {
    if (!onPackAll) return;
    onPackAll(customer?.unpacked_order_item_ids || []);
  };

  return (
    <View style={[styles.section]}>
      <TouchableOpacity
        activeOpacity={0.85}
        onPress={() => setOpen((v) => !v)}
        style={styles.headerRow}
      >
        <View style={[styles.accent]} />
        <View style={styles.headerLabelWrap}>
          <Text variant="caption_2" style={styles.headerLabel}>
            INVENTORY ({customers.length} customer
            {customers.length === 1 ? "" : "s"} · {totalUnits} unit
            {totalUnits === 1 ? "" : "s"})
          </Text>
          <Text variant="caption_2" style={styles.headerSub}>
            Spoke-stashed items · {totalPacked}/{totalUnits} packed
          </Text>
        </View>
        <View style={[styles.chev, open ? styles.chevUp : styles.chevDown]}>
          <ChevronRight
            width={12}
            height={12}
            pathFill={tokens.textSecondary}
          />
        </View>
      </TouchableOpacity>
      {open ? (
        <View style={styles.body}>
          {loading && customers.length === 0 ? (
            <View style={styles.center}>
              <ActivityIndicator color={tokens.textSecondary} />
            </View>
          ) : customers.length === 0 ? (
            <Text variant="caption_2" style={styles.empty}>
              No inventory customers on this route.
            </Text>
          ) : (
            customers.map((c) => (
              <CustomerCard
                key={String(c.cx_id)}
                customer={c}
                submitting={submitting}
                onPackCustomer={handlePackCustomer}
                onMarkSku={onMarkSku}
              />
            ))
          )}
        </View>
      ) : null}
    </View>
  );
};

export default InventorySection;

const styles = StyleSheet.create({
  section: { marginTop: tokens.spacing.large },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: tokens.spacing.page,
    paddingVertical: tokens.spacing.small,
  },
  accent: {
    width: 4,
    height: 28,
    borderRadius: 2,
    backgroundColor: tokens.info,
    marginRight: tokens.spacing.small,
  },
  headerLabel: {
    color: tokens.info,
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 0.5,
  },
  headerSub: { color: tokens.textSecondary, fontSize: 11, marginTop: 2 },
  headerLabelWrap: { flex: 1 },
  chev: {
    width: 16,
    height: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  chevDown: { transform: [{ rotate: "90deg" }] },
  chevUp: { transform: [{ rotate: "-90deg" }] },
  body: { marginTop: tokens.spacing.small },
  center: { paddingVertical: tokens.spacing.large, alignItems: "center" },
  empty: {
    color: tokens.textTertiary,
    fontSize: 12,
    textAlign: "center",
    paddingVertical: tokens.spacing.medium,
  },
  cxCard: {
    marginHorizontal: tokens.spacing.page,
    marginBottom: tokens.spacing.small,
    paddingVertical: tokens.spacing.medium,
  },
  cxHeader: {
    flexDirection: "row",
    alignItems: "center",
  },
  cxHeaderLeft: { flex: 1, marginRight: tokens.spacing.small },
  cxName: { color: tokens.textPrimary, fontWeight: "700", fontSize: 14 },
  cxMeta: { color: tokens.textSecondary, fontSize: 12, marginTop: 4 },
  cxStats: { color: tokens.textTertiary, fontSize: 11, marginTop: 2 },
  cxBody: {
    marginTop: tokens.spacing.medium,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: tokens.borderLight,
    paddingTop: tokens.spacing.medium,
  },
  skuRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: tokens.spacing.small,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: tokens.borderLight,
  },
  skuThumb: {
    width: 36,
    height: 36,
    borderRadius: tokens.radius.sm,
    marginRight: tokens.spacing.small,
  },
  skuThumbPlaceholder: { backgroundColor: tokens.borderLight },
  skuRowLeft: { flex: 1, marginRight: tokens.spacing.small },
  skuRowTitle: {
    color: tokens.textPrimary,
    fontWeight: "600",
    fontSize: 13,
  },
  skuRowMeta: { color: tokens.textSecondary, fontSize: 11, marginTop: 2 },
  markBtn: {
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: 6,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: tokens.warning,
    backgroundColor: tokens.surface,
  },
  markBtnText: { color: tokens.warning, fontSize: 12, fontWeight: "700" },
  packBtn: {
    marginTop: tokens.spacing.medium,
    paddingVertical: tokens.spacing.medium,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.primary.bg,
    alignItems: "center",
    justifyContent: "center",
  },
  packBtnIdle: { backgroundColor: tokens.borderLight },
  packBtnText: {
    color: tokens.primary.fg,
    fontSize: 13,
    fontWeight: "700",
  },
});
